import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const norm = (v) => (v || '').trim();
const same = (a, b) => norm(a).toLowerCase() === norm(b).toLowerCase();

// Add / rename / delete menu categories. Deleting keeps the products and moves them to another category.
const CategoryManager = ({ categories = [], items = [], onChange, selected, onSelect, mode = 'list', testId = 'cat' }) => {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null); // { original, value }
  const [confirm, setConfirm] = useState(null);

  const count = (c) => items.filter((i) => i.category === c).length;

  const add = () => {
    const name = norm(draft);
    if (!name) return toast.error('Escribe el nombre de la categoría');
    if (categories.some((c) => same(c, name))) return toast.error(`"${name}" ya existe en tu menú`);
    onChange({ categories: [...categories, name], items });
    setDraft('');
    toast.success(`Categoría "${name}" creada`);
  };

  const rename = () => {
    const name = norm(editing.value);
    if (!name) return toast.error('El nombre no puede quedar vacío');
    if (same(name, editing.original)) { setEditing(null); return; }
    if (categories.some((c) => same(c, name))) return toast.error(`"${name}" ya existe en tu menú`);
    onChange({
      categories: categories.map((c) => (c === editing.original ? name : c)),
      items: items.map((i) => (i.category === editing.original ? { ...i, category: name } : i)),
    });
    if (selected === editing.original) onSelect?.(name);
    setEditing(null);
    toast.success('Categoría renombrada');
  };

  const remove = () => {
    const target = confirm;
    const rest = categories.filter((c) => c !== target);
    const fallback = rest[0] || 'General';
    const moved = count(target);
    onChange({
      categories: rest.length ? rest : ['General'],
      items: items.map((i) => (i.category === target ? { ...i, category: fallback } : i)),
    });
    if (selected === target) onSelect?.('all');
    setConfirm(null);
    toast.success(moved ? `Categoría eliminada · ${moved} producto(s) movidos a "${fallback}"` : 'Categoría eliminada');
  };

  const Row = ({ c }) => {
    const active = selected === c;
    if (editing?.original === c) {
      return (
        <div className="flex items-center gap-1.5">
          <Input data-testid={`${testId}-rename-input`} autoFocus value={editing.value} className="h-9"
            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setEditing(null); }} />
          <button data-testid={`${testId}-rename-save`} onClick={rename} className="w-8 h-8 shrink-0 rounded-lg text-emerald-600 hover:bg-emerald-50 flex items-center justify-center"><Check size={16} /></button>
          <button data-testid={`${testId}-rename-cancel`} onClick={() => setEditing(null)} className="w-8 h-8 shrink-0 rounded-lg text-gray-400 hover:bg-gray-100 flex items-center justify-center"><X size={16} /></button>
        </div>
      );
    }
    const controls = (
      <>
        <button data-testid={`${testId}-edit-${c}`} title="Renombrar" onClick={(e) => { e.stopPropagation(); setEditing({ original: c, value: c }); }}
          className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center ${active ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-[#1E2130]'}`}><Pencil size={13} /></button>
        <button data-testid={`${testId}-delete-${c}`} title="Eliminar" onClick={(e) => { e.stopPropagation(); setConfirm(c); }}
          className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center ${active ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-red-500'}`}><Trash2 size={13} /></button>
      </>
    );
    if (mode === 'chips') {
      return (
        <span data-testid={`${testId}-chip-${c}`} className="inline-flex items-center gap-1 bg-[#F7F8FA] border border-gray-200 rounded-full pl-3 pr-1 py-1 text-sm">
          {c} <span className="text-gray-400 text-xs">({count(c)})</span> {controls}
        </span>
      );
    }
    return (
      <div className={`flex items-center rounded-lg ${active ? 'bg-[#EE3B36] text-white' : 'hover:bg-gray-50'}`}>
        <button data-testid={`${testId}-select-${c}`} onClick={() => onSelect?.(c)} className="flex-1 min-w-0 text-left px-3 py-2 text-sm font-medium truncate">{c} ({count(c)})</button>
        {controls}
      </div>
    );
  };

  const form = (
    <div className="flex gap-2">
      <Input data-testid={`${testId}-new`} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Nueva categoría" className="h-9"
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      <Button data-testid={`${testId}-add`} type="button" variant="outline" className="h-9 shrink-0" onClick={add}><Plus size={16} /></Button>
    </div>
  );

  return (
    <div data-testid={`${testId}-manager`}>
      {mode === 'chips' ? (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            {categories.map((c) => <Row key={c} c={c} />)}
            {categories.length === 0 && <p className="text-sm text-gray-400">Sin categorías todavía. Crea la primera.</p>}
          </div>
          {form}
        </>
      ) : (
        <div className="space-y-1.5">
          {onSelect && (
            <button data-testid={`${testId}-select-all`} onClick={() => onSelect('all')} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${selected === 'all' ? 'bg-[#EE3B36] text-white' : 'hover:bg-gray-50'}`}>Todos ({items.length})</button>
          )}
          {categories.map((c) => <Row key={c} c={c} />)}
          {categories.length === 0 && <p className="text-sm text-gray-400 px-3 py-2">Sin categorías todavía.</p>}
          <div className="pt-2">{form}</div>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar la categoría "{confirm}"</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm && count(confirm) > 0
                ? `Tiene ${count(confirm)} producto(s). No se borrarán: se moverán a "${categories.filter((c) => c !== confirm)[0] || 'General'}".`
                : 'Esta categoría está vacía y se eliminará del menú.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testId}-delete-confirm`} className="bg-red-600 hover:bg-red-700" onClick={remove}>Eliminar categoría</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoryManager;
